/**
 * The comms feature's story port (M7 plan S31). The story link (S30, `ServiceMap.story`) is built in parallel and
 * is not on this branch, so the feature talks to the story through this small interface, which the lander wires:
 *
 * - `bindCommsStory(port)` once the story runtime exists: the panel reads nodes (`view`) and applies picks
 *   (`choose`) through it.
 * - `postStoryEffects(result.effects)` after every story result: the `comms` effects and the comms-channel
 *   `callback` effects become waiting hails; every other effect is ignored here.
 *
 * Module state, like M6's fleet alert hooks (`bindFleetAlertHooks`). Event rate, never per frame, except
 * `takeHailEffects`, which allocates nothing when the inbox is empty.
 */
import type { CommsView } from '../../story/comms'
import type { StoryEffect } from '../../story/types'

/** What the comms panel needs from the story runtime (S30 adapts the S9 engine on the live Career to it). */
export interface CommsStory {
  /** The node as the player sees it now (S9 `viewNode` on the live Career): Show replies already gated. Null = unknown. */
  view(nodeId: string): CommsView | null
  /**
   * Applies a picked reply (S9 `chooseReply`, then the story runtime runs its `commsReply` event, effects and
   * autosave). Returns the next node id, null when the conversation ends, or undefined when the pick was refused
   * (an unknown node or reply, or a Show reply whose log is gone): the panel then stays on the node.
   */
  choose(nodeId: string, replyId: string): string | null | undefined
  /** The display title of a held log, for a Show reply's "Shows:" line (null or absent = the reply text only). */
  logTitle?(logId: string): string | null
}

/** A story effect that becomes a hail: a comms node, or a one-line comms callback. */
export type HailEffect = Extract<StoryEffect, { type: 'comms' }> | (Extract<StoryEffect, { type: 'callback' }> & { channel: 'comms' })

let story: CommsStory | null = null
const inbox: HailEffect[] = []

/** Binds the story runtime (null unbinds). Returns the unbind, which only clears this binding. */
export function bindCommsStory(port: CommsStory | null): () => void {
  story = port
  return () => {
    if (story === port) story = null
  }
}

export function commsStory(): CommsStory | null {
  return story
}

/** True for the effects the comms feature shows. */
export function isHailEffect(e: StoryEffect): e is HailEffect {
  return e.type === 'comms' || (e.type === 'callback' && e.channel === 'comms')
}

/** Queues a story result's hail effects (the rest are other features'). Call after every story result. */
export function postStoryEffects(effects: readonly StoryEffect[]): void {
  for (const e of effects) if (isHailEffect(e)) inbox.push(e)
}

/** Hands every queued hail effect to `fn`, oldest first, and empties the inbox. */
export function takeHailEffects(fn: (e: HailEffect) => void): void {
  if (inbox.length === 0) return
  const batch = inbox.splice(0)
  for (const e of batch) fn(e)
}

/** Drops queued effects and the binding (tests, feature dispose). */
export function resetCommsPort(): void {
  inbox.length = 0
  story = null
}
